import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Plus,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const EditTimesheet = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { success, error: showError } = useToast();
  
  const [formData, setFormData] = useState({
    employee: '',
    department: '',
    weekStarting: '',
    weekEnding: '',
    status: 'draft',
    timeEntries: [],
    totalHours: 0,
    billableHours: 0,
    nonBillableHours: 0,
    notes: '',
    submittedAt: null,
    approvedBy: '',
    approvedAt: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeDayTab, setActiveDayTab] = useState(0);

  const normalizeEntries = (entries = []) => {
    return entries.map((day) => {
      const dateValue = day.date ? new Date(day.date).toISOString().split('T')[0] : '';
      return {
        id: `day-${dateValue || Math.random().toString(36).slice(2)}`,
        date: dateValue,
        dayName: day.dayName || (dateValue ? new Date(dateValue).toLocaleDateString('en-US', { weekday: 'long' }) : ''),
        entries: (day.entries || []).map((entry, index) => ({
          id: entry.id || `entry-${dateValue || index}-${index}`,
          project: entry.project || '',
          task: entry.task || '',
          description: entry.description || '',
          startTime: entry.startTime || '09:00',
          endTime: entry.endTime || '17:00',
          duration: entry.duration || 0,
          billable: entry.billable ?? true,
          hourlyRate: entry.hourlyRate || 0
        }))
      };
    });
  };

  const generateWeekEntries = (startDate, endDate) => {
    const entries = [];
    if (!startDate || !endDate) return entries;
    const start = new Date(startDate);
    const end = new Date(endDate);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      entries.push({
        id: `day-${d.toISOString().split('T')[0]}`,
        date: d.toISOString().split('T')[0],
        dayName: d.toLocaleDateString('en-US', { weekday: 'long' }),
        entries: [
          {
            id: `entry-${d.toISOString().split('T')[0]}-1`,
            project: '',
            task: '',
            description: '',
            startTime: '09:00',
            endTime: '17:00',
            duration: 8,
            billable: true,
            hourlyRate: 75
          }
        ]
      });
    }

    return entries;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [timesheetResponse, projectsResponse, membersResponse] = await Promise.all([
          fetch(`${API_CONFIG.ENDPOINTS.TIMESHEETS}/${id}`, { headers: API_CONFIG.HEADERS }),
          fetch(API_CONFIG.ENDPOINTS.PROJECTS, { headers: API_CONFIG.HEADERS }),
          fetch(API_CONFIG.ENDPOINTS.TEAM_MEMBERS, { headers: API_CONFIG.HEADERS })
        ]);

        if (!timesheetResponse.ok) {
          const errorData = await timesheetResponse.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to load timesheet');
        }

        const timesheetData = await timesheetResponse.json();
        const timesheet = timesheetData.data || {};

        if (projectsResponse.ok) {
  const data = await projectsResponse.json();

  const projectsArray =
    Array.isArray(data?.data?.projects)
      ? data.data.projects
      : Array.isArray(data?.projects)
      ? data.projects
      : Array.isArray(data?.data)
      ? data.data
      : [];

  setProjects(projectsArray);
}


        if (membersResponse.ok) {
          const data = await membersResponse.json();
          setEmployees(data.data || []);
        }

        const weekStarting = timesheet.weekStarting ? new Date(timesheet.weekStarting).toISOString().split('T')[0] : '';
        const weekEnding = timesheet.weekEnding ? new Date(timesheet.weekEnding).toISOString().split('T')[0] : '';

        const normalizedEntries = normalizeEntries(timesheet.timeEntries);
        const timeEntries = normalizedEntries.length
          ? normalizedEntries
          : generateWeekEntries(weekStarting, weekEnding);

        setFormData({
          employee: timesheet.employee || '',
          employeeName: timesheet.employeeName || '',
          department: timesheet.department || '',
          weekStarting,
          weekEnding,
          status: timesheet.status || 'draft',
          timeEntries,
          totalHours: timesheet.totalHours || 0,
          billableHours: timesheet.billableHours || 0,
          nonBillableHours: timesheet.nonBillableHours || 0,
          notes: timesheet.notes || '',
          submittedAt: timesheet.submittedAt || null,
          approvedBy: timesheet.approvedBy || '',
          approvedAt: timesheet.approvedAt || null
        });

        if (weekStarting) {
          setCurrentWeek(new Date(weekStarting));
        }
      } catch (err) {
        showError(err.message || 'Failed to load timesheet');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTimeEntryChange = (dayId, entryId, field, value) => {
    setFormData(prev => ({
      ...prev,
      timeEntries: prev.timeEntries.map(day => 
        day.id === dayId 
          ? {
              ...day,
              entries: day.entries.map(entry => 
                entry.id === entryId 
                  ? { ...entry, [field]: value }
                  : entry
              )
            }
          : day
      )
    }));
  };

  const handleAddTimeEntry = (dayId) => {
    const newEntry = {
      id: `entry-${dayId}-${Date.now()}`,
      project: '',
      task: '',
      description: '',
      startTime: '09:00',
      endTime: '17:00',
      duration: 8,
      billable: true,
      hourlyRate: 75
    };

    setFormData(prev => ({
      ...prev,
      timeEntries: prev.timeEntries.map(day => 
        day.id === dayId 
          ? { ...day, entries: [...day.entries, newEntry] }
          : day
      )
    }));
  };

  const handleRemoveTimeEntry = (dayId, entryId) => {
    setFormData(prev => ({
      ...prev,
      timeEntries: prev.timeEntries.map(day => 
        day.id === dayId 
          ? { ...day, entries: day.entries.filter(entry => entry.id !== entryId) }
          : day
      )
    }));
  };

  const calculateTotals = () => {
    let totalHours = 0;
    let billableHours = 0;
    let nonBillableHours = 0;

    formData.timeEntries.forEach(day => {
      day.entries.forEach(entry => {
        totalHours += parseFloat(entry.duration) || 0;
        if (entry.billable) {
          billableHours += parseFloat(entry.duration) || 0;
        } else {
          nonBillableHours += parseFloat(entry.duration) || 0;
        }
      });
    });

    setFormData(prev => ({
      ...prev,
      totalHours,
      billableHours,
      nonBillableHours
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [formData.timeEntries]);

  const handleWeekChange = (direction) => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + (direction * 7));
    setCurrentWeek(newWeek);
    
    const startOfWeek = new Date(newWeek);
    startOfWeek.setDate(newWeek.getDate() - newWeek.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    setFormData(prev => ({
      ...prev,
      weekStarting: startOfWeek.toISOString().split('T')[0],
      weekEnding: endOfWeek.toISOString().split('T')[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const selectedEmployee = employees.find((member) => (member._id || member.id) === formData.employee);

      const payload = {
  employee: selectedEmployee?._id || selectedEmployee?.id,
  department: formData.department,
  weekStarting: formData.weekStarting,
  weekEnding: formData.weekEnding,
  status: formData.status,
  notes: formData.notes,

  timeEntries: formData.timeEntries.map((day) => ({
    date: day.date,
    entries: day.entries.map((entry) => ({
      project: entry.project || null, // IMPORTANT
      task: entry.task || '',
      description: entry.description || '',
      startTime: entry.startTime,
      endTime: entry.endTime,
      duration: Number(entry.duration) || 0,
      billable: Boolean(entry.billable),
      hourlyRate: Number(entry.hourlyRate) || 0
    }))
  }))
};


      const response = await fetch(`${API_CONFIG.ENDPOINTS.TIMESHEETS}/${id}`, {
        method: 'PUT',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update timesheet');
      }

      success('Timesheet updated successfully!');
      navigate('/projects/time-tracking/timesheets');
    } catch (err) {
      showError(err.message || 'Failed to update timesheet. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader 
          title="Edit Timesheet"
          description="Loading timesheet details..."
        />
        <div className="page-container">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Edit Timesheet"
        description="Update timesheet details and weekly time entries"
      />

      <style>
{`
  .timesheet-week-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: nowrap;
  }

  .timesheet-week-selector .week-info {
    flex: 1;
    text-align: center;
    white-space: nowrap;
  }

  .timesheet-week-selector button {
    white-space: nowrap;
    flex-shrink: 0;
  }
`}
</style>

      <div className="page-container">
        <div className="project-create-container">
          <form onSubmit={handleSubmit}>
            <div className="project-form-card">
              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Timesheet Information</h3>
                  <p className="section-description">Basic information about the timesheet</p>
                </div>
                
                <div className="form-grid-4">
                  <div className="form-group">
                    <label className="form-label">Employee *</label>
                    <select
                      className="form-select"
                      value={formData.employee}
                      onChange={(e) => {
                        const value = e.target.value;
                        const selectedEmployee = employees.find((member) => (member._id || member.id) === value);
                        setFormData((prev) => ({
                          ...prev,
                          employee: value,
                          department: selectedEmployee?.department || prev.department
                        }));
                      }}
                      required
                    >
                      <option value="">Select Employee</option>
                      {employees.map(employee => (
                        <option key={employee._id || employee.id} value={employee._id || employee.id}>
                          {employee.name} {employee.department ? `- ${employee.department}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Week Starting *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.weekStarting}
                      onChange={(e) => handleInputChange('weekStarting', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Week Ending *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.weekEnding}
                      onChange={(e) => handleInputChange('weekEnding', e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <option value="draft">Draft</option>
                      <option value="submitted">Submitted</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Weekly Overview</h3>
                  <p className="section-description">Navigate weeks and review totals</p>
                </div>
                <div className="timesheet-week-selector">
                  <button
                    type="button"
                    className="btn btn-outline d-flex align-items-center"
                    onClick={() => handleWeekChange(-1)}
                  >
                    <ChevronLeft size={16} />
                    Previous Week
                  </button>
                  <div className="week-info">
                    <h4 className="mb-0">
                      Week of {formData.weekStarting ? new Date(formData.weekStarting).toLocaleDateString() : ''} - {formData.weekEnding ? new Date(formData.weekEnding).toLocaleDateString() : ''}
                    </h4>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline d-flex align-items-center"
                    onClick={() => handleWeekChange(1)}
                  >
                    Next Week
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Time Entries</h3>
                  <p className="section-description">Log your daily time entries for the week</p>
                </div>
                
                <div className="timesheet-tabs-container">
                  <div className="timesheet-tabs">
                    {formData.timeEntries.map((day, index) => (
                      <button
                        key={day.id}
                        type="button"
                        className={`timesheet-tab ${activeDayTab === index ? 'active' : ''}`}
                        onClick={() => setActiveDayTab(index)}
                      >
                        <div className="tab-day-name">{day.dayName}</div>
                        <div className="tab-date">{day.date ? new Date(day.date).getDate() : ''}</div>
                      </button>
                    ))}
                  </div>
                  
                  <div className="timesheet-tab-content">
                    {formData.timeEntries.map((day, dayIndex) => (
                      <div 
                        key={day.id} 
                        className={`timesheet-tab-panel ${activeDayTab === dayIndex ? 'active' : ''}`}
                      >
                        <div className="day-timesheet">
                          <div className="day-header">
                            <h5 className="day-title">
                              {day.dayName} {day.date ? `- ${new Date(day.date).toLocaleDateString()}` : ''}
                            </h5>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm d-flex align-items-center"
                              onClick={() => handleAddTimeEntry(day.id)}
                            >
                              <Plus size={16} />
                              Add Entry
                            </button>
                          </div>
                          
                          <div className="time-entries">
                            {day.entries.map((entry) => (
                              <div key={entry.id} className="time-entry-row">
                                <div className="row">
                                  <div className="col-md-3">
                                    <div className="form-group">
                                      <label className="form-label">Project</label>
                                      <select
                                        className="form-select"
                                        value={entry.project}
                                        onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'project', e.target.value)}
                                      >
                                        <option value="">Select Project</option>
                                        {Array.isArray(projects) && projects.map(project => (
                                          <option key={project._id || project.id} value={project._id || project.id}>
                                            {project.name}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                  
                                  <div className="col-md-2">
                                    <div className="form-group">
                                      <label className="form-label">Start Time</label>
                                      <input
                                        type="time"
                                        className="form-control"
                                        value={entry.startTime}
                                        onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'startTime', e.target.value)}
                                      />
                                    </div>
                                  </div>
                                  
                                  <div className="col-md-2">
                                    <div className="form-group">
                                      <label className="form-label">End Time</label>
                                      <input
                                        type="time"
                                        className="form-control"
                                        value={entry.endTime}
                                        onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'endTime', e.target.value)}
                                      />
                                    </div>
                                  </div>
                                  
                                  <div className="col-md-1">
                                    <div className="form-group">
                                      <label className="form-label">Hours</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        value={entry.duration}
                                        onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'duration', e.target.value)}
                                        step="0.25"
                                        min="0"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div className="col-md-2">
                                    <div className="form-group">
                                      <label className="form-label">Rate ($)</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        value={entry.hourlyRate}
                                        onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'hourlyRate', e.target.value)}
                                        step="0.01"
                                        min="0"
                                      />
                                    </div>
                                  </div>
                                  
                                  <div className="col-md-1">
                                    <div className="form-group">
                                      <label className="form-label">Billable</label>
                                      <div className="form-check">
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          checked={entry.billable}
                                          onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'billable', e.target.checked)}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="col-md-1">
                                    <div className="form-group">
                                      <label className="form-label">&nbsp;</label>
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => handleRemoveTimeEntry(day.id, entry.id)}
                                        disabled={day.entries.length === 1}
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Summary</h3>
                  <p className="section-description">Totals for the selected week</p>
                </div>
                
                <div className="form-grid-4">
                  <div className="form-group">
                    <label className="form-label">Total Hours</label>
                    <input type="text" className="form-control" value={formData.totalHours} readOnly />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Billable Hours</label>
                    <input type="text" className="form-control" value={formData.billableHours} readOnly />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Non-Billable Hours</label>
                    <input type="text" className="form-control" value={formData.nonBillableHours} readOnly />
                  </div>
                </div>

                <div className="form-group mt-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="project-form-actions">
              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center"
                onClick={() => navigate('/projects/time-tracking/timesheets')}
              >
                <ArrowLeft size={16} className="me-2" />
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center"
                disabled={isSubmitting}
              >
                <Save size={16} className="me-2" />
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditTimesheet;
